import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../AuthContext'; // 인증 컨텍스트 가져오기

// 소켓 컨텍스트 생성
const SocketContext = createContext(null);

// 소켓 상태 관리 훅 생성
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket는 SocketProvider 내부에서만 사용할 수 있습니다');
  }
  return context;
};

// 소켓 제공자 컴포넌트
export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const { user } = useAuth(); // 인증된 사용자 정보 가져오기

  // 소켓 연결 초기화
  useEffect(() => {
    let socketInstance = null;

    try {
      // 소켓 서버 연결 - 로그인 여부와 상관없이 연결
      socketInstance = io(window.location.origin, {
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });

      // 연결 성공 이벤트
      socketInstance.on('connect', () => {
        console.log('소켓 서버에 연결되었습니다', socketInstance.id);
        setIsConnected(true);
        setConnectionError(null);

        // 로그인된 사용자인 경우에만 인증 시도
        if (user && user.user_id) {
          socketInstance.emit('authenticate', {
            userId: user.user_id
          });
        }
      });

      // 인증 성공 이벤트
      socketInstance.on('authenticated', (response) => {
        if (response.success) {
          console.log('소켓 인증이 완료되었습니다');
        }
      });

      // 인증 오류 이벤트
      socketInstance.on('auth_error', (error) => {
        console.error('소켓 인증 오류:', error.message);
        setConnectionError(error.message);
      });

      // 연결 오류 이벤트
      socketInstance.on('connect_error', (error) => {
        console.error('소켓 연결 오류:', error.message);
        setIsConnected(false);
        setConnectionError(error.message);
      });

      // 연결 재시도 이벤트
      socketInstance.on('reconnect_attempt', (attemptNumber) => {
        console.log(`소켓 연결 재시도 중... (${attemptNumber})`);
      });

      // 연결 재시도 성공 이벤트
      socketInstance.on('reconnect', (attemptNumber) => {
        console.log(`소켓 연결이 복구되었습니다 (${attemptNumber}회 시도 후)`);
        setIsConnected(true);
        
        // 로그인된 사용자인 경우에만 재인증
        if (user && user.user_id) {
          socketInstance.emit('authenticate', {
            userId: user.user_id
          });
        }
      });

      // 연결 재시도 실패 이벤트
      socketInstance.on('reconnect_failed', () => {
        console.error('소켓 연결 재시도에 실패했습니다');
        setConnectionError('서버에 연결할 수 없습니다. 페이지를 새로고침 해주세요.');
      });

      // 연결 해제 이벤트
      socketInstance.on('disconnect', (reason) => {
        console.log('소켓 연결이 끊겼습니다:', reason);
        setIsConnected(false);
      });

      // 상태 업데이트
      setSocket(socketInstance);
    } catch (error) {
      console.error('소켓 초기화 오류:', error);
      setConnectionError('소켓 연결 초기화 중 오류가 발생했습니다');
    }

    // 컴포넌트 언마운트 시 소켓 연결 해제
    return () => {
      if (socketInstance) {
        console.log('소켓 연결 해제 중...');
        socketInstance.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
    };
  }, [user]); // 사용자 정보가 변경될 때마다 소켓 재연결

  // 화상 채팅 방 참여 함수
  const joinVideoRoom = (roomId) => {
    if (socket && isConnected && roomId) {
      socket.emit('join room', roomId);
      console.log(`화상 채팅방 ${roomId}에 참여했습니다`);
    }
  };

  // 화상 채팅 방 나가기 함수
  const leaveVideoRoom = (roomId) => {
    if (socket && isConnected && roomId) {
      socket.emit('leave room', roomId);
      console.log(`화상 채팅방 ${roomId}에서 나갔습니다`);
    }
  };

  // 기존 함수들...
  const joinRoom = (roomId) => {
    if (socket && isConnected && roomId) {
      socket.emit('join_room', { roomId });
      console.log(`채팅방 ${roomId}에 입장했습니다`);
    }
  };

  const leaveRoom = (roomId) => {
    if (socket && isConnected && roomId) {
      socket.emit('leave_room', { roomId });
      console.log(`채팅방 ${roomId}에서 퇴장했습니다`);
    }
  };

  const sendMessage = (roomId, content, messageType = 'text', fileInfo = null) => {
    return new Promise((resolve, reject) => {
      if (socket && isConnected && roomId) {
        const messageData = {
          roomId,
          content,
          messageType,
          fileInfo
        };

        socket.emit('send_message', messageData);
        resolve(true);
      } else {
        reject(new Error('소켓이 연결되어 있지 않습니다'));
      }
    });
  };

  const sendTypingStatus = (roomId, isTyping) => {
    if (socket && isConnected && roomId) {
      socket.emit('typing', { roomId, isTyping });
    }
  };

  const markMessageAsRead = (roomId, messageId) => {
    if (socket && isConnected && roomId && messageId) {
      socket.emit('mark_as_read', { roomId, messageId });
    }
  };

  const sendFriendRequest = (recipientId) => {
    if (socket && isConnected && recipientId) {
      socket.emit('friend_request', { recipientId });
    }
  };

  const sendCallRequest = (recipientId, roomId, callType = 'video') => {
    return new Promise((resolve, reject) => {
      if (socket && isConnected && recipientId) {
        socket.emit('call_request', { recipientId, roomId, callType });
        resolve(true);
      } else {
        reject(new Error('소켓이 연결되어 있지 않습니다'));
      }
    });
  };

  const sendCallResponse = (callerId, accepted, roomId) => {
    if (socket && isConnected && callerId) {
      socket.emit('call_response', { callerId, accepted, roomId });
    }
  };

  // 컨텍스트 값
  const value = {
    socket,
    isConnected,
    connectionError,
    joinRoom,
    leaveRoom,
    joinVideoRoom,
    leaveVideoRoom,
    sendMessage,
    sendTypingStatus,
    markMessageAsRead,
    sendFriendRequest,
    sendCallRequest,
    sendCallResponse
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;